<script setup>
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import Loading from '../Loading.vue';
import IconBack from '../icons/IconBack.vue';
import XlCard from '../xl/XlCard.vue';
import XlTable from '../xl/XlTable.vue';
import XlTag from '../xl/XlTag.vue';
import { setPageTitle } from '../../composables/useHubPageTitle';
import { money } from '../../helpers/coverage';
import { longDate } from '../../helpers/usDate';

const route = useRoute();

const record = ref(null);
const loading = ref(true);
const missing = ref(false);
const failed = ref(false);

const name = computed(() => [record.value?.first_name, record.value?.last_name].filter(Boolean).join(' ') || record.value?.email);
const brokerName = computed(() => {
  const broker = record.value?.broker;

  return broker ? [broker.first_name, broker.last_name].filter(Boolean).join(' ') || broker.name : '';
});
const status = computed(() => (record.value?.kind === 'granted' ? 'Not invited yet' : 'Invited'));

onMounted(async () => {
  try {
    const { data } = await axios.get(`${window.store?.event_backend_api ?? '/api'}/policyholders/invited/${encodeURIComponent(route.params.id)}`);
    record.value = data.data;
    setPageTitle(name.value);
  } catch (e) {
    missing.value = e.response?.status === 404;
    failed.value = !missing.value;
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <Loading v-if="loading" variant="rows" :rows="4" />

  <div v-else-if="missing" class="invited" data-xl-gen="northstar" data-testid="invited-missing">
    <XlCard padded>
      <p>This policyholder isn't waiting to register any more.</p>
      <RouterLink to="/policyholders" class="invited__back-link"><IconBack /> Back to Policyholders</RouterLink>
    </XlCard>
  </div>

  <div v-else-if="failed" class="invited" data-xl-gen="northstar" data-testid="invited-failed">
    <XlCard padded><p>Couldn't load this policyholder right now.</p></XlCard>
  </div>

  <div v-else class="invited" data-xl-gen="northstar">
    <header class="invited__head">
      <h1 data-testid="invited-name">{{ name }}</h1>
      <p>{{ record.email }}</p>
      <XlTag :tone="record.kind === 'granted' ? '' : 'warning'">{{ status }}</XlTag>
    </header>

    <XlCard padded>
      <dl class="invited__facts">
        <div><dt>Phone</dt><dd>{{ record.phone || '—' }}</dd></div>
        <div><dt>Broker</dt><dd>{{ brokerName || 'No broker' }}</dd></div>
      </dl>
    </XlCard>

    <XlCard class="invited__policies">
      <h2>Policies</h2>
      <p v-if="record.policies.unavailable" class="invited__note" data-testid="invited-policies-unavailable">Policies couldn't be loaded right now.</p>
      <p v-else-if="!record.policies.items.length" class="invited__note" data-testid="invited-policies-empty">No imported policies</p>
      <XlTable v-else>
        <template #head>
          <th>Line</th>
          <th>Policy number</th>
          <th>Monthly premium</th>
          <th>Renewal date</th>
        </template>
        <tr v-for="policy in record.policies.items" :key="policy.id" data-testid="invited-policy">
          <td>{{ policy.line?.name || 'Policy' }}</td>
          <td>{{ policy.policy_number || '—' }}</td>
          <td>{{ money(policy.monthly_premium, policy.currency) || '—' }}</td>
          <td>{{ longDate(policy.renewal_date) || 'No date' }}</td>
        </tr>
      </XlTable>
    </XlCard>
  </div>
</template>

<style scoped>
.invited__back-link { display: inline-flex; align-items: center; gap: 8px; color: var(--xl-color-primary-text); text-decoration: none; }
.invited__back-link:visited, .invited__back-link:hover, .invited__back-link:focus { color: var(--xl-color-primary-text); }
.invited__back-link:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
.invited {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: var(--setup-page-padding) var(--setup-page-padding) 60px;
  font-family: var(--xl-font-family);
}

.invited__head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 14px; }
.invited__head h1 { margin: 0; font-size: var(--xl-page-title-size); font-weight: 600; color: var(--xl-color-primary-text); }
.invited__head p { margin: 0; flex: 1 0 100%; order: 2; color: var(--xl-color-outline); }
.invited__facts { display: flex; flex-wrap: wrap; gap: 24px 48px; margin: 0; }
.invited__facts dt { font-size: 12.5px; color: var(--xl-color-outline); }
.invited__facts dd { margin: 2px 0 0; overflow-wrap: anywhere; }
.invited__policies h2 { margin: 0; padding: 18px 22px 8px; font-size: 16px; }
.invited__note { margin: 0; padding: 0 22px 18px; color: var(--xl-color-outline); }
</style>
